import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { uploadPdf } from '../api';
import { pdfResultToDraft } from '../import/pdfImport';
import { findRiseCourses, importRiseCourse, sourceFromFileList } from '../import/riseImport';
import { readZip } from '../import/zipReader';
import Button from './ui/Button';
import FileButton from './ui/FileButton';

/**
 * "Neu importieren" in the editors: the author picks a PDF or a Rise course
 * and gets its text in place of the current one. It runs the same
 * conversions as "Inhalt erstellen" (import/pdfImport.js,
 * import/riseImport.js) and hands the draft ({ text, files, warnings, ... })
 * to `onImported`; the editor stages its files like hand-inserted ones, so
 * nothing is stored before the author saves. A folder or ZIP with several
 * courses asks which one.
 */
function ReimportDialog({ onClose, onImported }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [courses, setCourses] = useState(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape' && !busy) {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [busy, onClose]);

  async function run(task) {
    setBusy(true);
    setError('');

    try {
      await task();
    } catch (err) {
      console.error('Re-import failed:', err);
      setError(err?.message || t('reimport.error'));
    } finally {
      setBusy(false);
    }
  }

  function handlePdf(event) {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (file.type !== 'application/pdf') {
      setError(t('reimport.pdfOnly'));
      return;
    }

    run(async () => {
      onImported(await pdfResultToDraft(await uploadPdf(file)));
    });
  }

  function handleCourse(loadSource) {
    run(async () => {
      const found = await findRiseCourses(await loadSource());

      if (found.length === 0) {
        throw new Error(t('createArticleRise.noCourse'));
      }

      if (found.length === 1) {
        onImported(await importRiseCourse(await found[0].open()));
        return;
      }

      setCourses(found);
    });
  }

  function handleFolder(event) {
    const { files } = event.target;
    if (files?.length) {
      handleCourse(async () => sourceFromFileList(files));
    }
    event.target.value = '';
  }

  function handleZip(event) {
    const file = event.target.files?.[0];
    if (file) {
      handleCourse(() => readZip(file));
    }
    event.target.value = '';
  }

  function pickCourse(course) {
    run(async () => {
      onImported(await importRiseCourse(await course.open()));
    });
  }

  return (
    <div
      className="reimport-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        className="reimport-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reimport-dialog-title"
        tabIndex={-1}
      >
        <div className="reimport-dialog__head">
          <h2 id="reimport-dialog-title">{t('reimport.title')}</h2>

          <button
            type="button"
            className="reimport-dialog__close"
            aria-label={t('reimport.close')}
            disabled={busy}
            onClick={onClose}
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>

        <p className="reimport-dialog__note">{t('reimport.note')}</p>

        {!mode && (
          <div className="reimport-dialog__choices">
            <Button variant="choice" onClick={() => setMode('pdf')}>
              <i className="fa-solid fa-file-pdf" />
              {t('reimport.pdf')}
            </Button>

            <Button variant="choice" onClick={() => setMode('course')}>
              <i className="fa-solid fa-graduation-cap" />
              {t('reimport.course')}
            </Button>
          </div>
        )}

        {mode === 'pdf' && (
          <div className="reimport-dialog__choices">
            <FileButton variant="choice" accept="application/pdf" disabled={busy} onChange={handlePdf}>
              <i className="fa-solid fa-upload" />
              {t('createArticleUpload.selectFileButton')}
            </FileButton>
          </div>
        )}

        {mode === 'course' && !courses && (
          <div className="reimport-dialog__choices">
            <FileButton variant="choice" directory disabled={busy} onChange={handleFolder}>
              <i className="fa-solid fa-folder-open" />
              {t('createArticleRise.folderButton')}
            </FileButton>

            <FileButton variant="choice" accept=".zip,application/zip" disabled={busy} onChange={handleZip}>
              <i className="fa-solid fa-file-zipper" />
              {t('createArticleRise.zipButton')}
            </FileButton>
          </div>
        )}

        {courses && (
          <>
            <p>{t('reimport.pickCourse', { count: courses.length })}</p>
            <ul className="reimport-dialog__courses">
              {courses.map((course, index) => (
                <li key={`${course.name}-${index}`}>
                  <button type="button" disabled={busy} onClick={() => pickCourse(course)}>
                    <span>{course.title}</span>
                    {course.name && course.name !== course.title && <small>{course.name}</small>}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {busy && <p role="status">{t('reimport.processing')}</p>}

        {error && (
          <div className="login-message login-message-error" role="alert">
            <i className="fa-solid fa-circle-exclamation" />
            <span>{error}</span>
          </div>
        )}

        {mode && !busy && (
          <div className="reimport-dialog__footer">
            <Button
              variant="secondary"
              onClick={() => {
                setMode(null);
                setCourses(null);
                setError('');
              }}
            >
              {t('reimport.back')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReimportDialog;
